import _ from "lodash";
import React, { useRef, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { useQuery, useQueryCache } from "react-query";
import { useMedia } from "react-media";
import { Segment, Header, Divider } from "semantic-ui-react";
import { useParams } from "react-router-dom";
import { PublicWrapper } from "../../components/PublicWrapper";
import AccountInfo from "../../components/AccountInfo";
import LoginRegisterCard from "../../components/LoginRegisterCard";
import networkSelectors from "../../selectors/network";
import { getUser } from "../../actions/identity";
import identitySelectors from "../../selectors/identity";
import "./CitizenAccount.scss";
import { mixPanelAgencyOnly } from "../../mixpanel/general_functions";
import useCountries from "../../hooks/useCountries";
import { FullAddress } from "../../utils/FullAddress";
import { FullPhysicalAddress } from "../../utils/FullPhysicalAddress";

export const CitizenAccount = () => {
  const isLoggedIn = useSelector(identitySelectors.isLoggedIn);
  const { site } = useParams<{ site: string }>();
  const networkHeaders = useSelector(networkSelectors.defaultHeaders);
  const contentRef = useRef(null);
  const user = useSelector(identitySelectors.getUser);
  const userQuery = useQuery(["user"], getUser(networkHeaders));
  const { agencyCountry } = useCountries();
  const address = useMemo(() => _.get(userQuery.data, "data.attributes.address"), [userQuery.data]);
  const address2 = useMemo(() => _.get(userQuery.data, "data.attributes.address2"), [userQuery.data]);
  const city = useMemo(() => _.get(userQuery.data, "data.attributes.city"), [userQuery.data]);
  const state = useMemo(() => _.get(userQuery.data, "data.attributes.state"), [userQuery.data]);
  const zip = useMemo(() => _.get(userQuery.data, "data.attributes.zip"), [userQuery.data]);
  const countryId = useMemo(() => _.get(userQuery.data, "data.attributes.country-id"), [userQuery.data]);
  const physicalAddress = useMemo(() => _.get(userQuery.data, "data.attributes.home-address"), [userQuery.data]);
  const physicalAddress2 = useMemo(() => _.get(userQuery.data, "data.attributes.home-address2"), [userQuery.data]);
  const physicalCity = useMemo(() => _.get(userQuery.data, "data.attributes.home-city"), [userQuery.data]);
  const physicalState = useMemo(() => _.get(userQuery.data, "data.attributes.home-state"), [userQuery.data]);
  const physicalZip = useMemo(() => _.get(userQuery.data, "data.attributes.home-zip"), [userQuery.data]);
  const homeCountryId = useMemo(() => _.get(userQuery.data, "data.attributes.home-country-id"), [userQuery.data]);

  const showCountryFlag = !!agencyCountry && countryId !== agencyCountry?.id;
  const showHomeCountryFlag = !!agencyCountry && homeCountryId !== agencyCountry?.id;

  const sameMailAndPhysicalAddress = useMemo(
    () => _.get(userQuery.data, "data.attributes.same-mail-and-physical-address"),
    [userQuery.data],
  );
  const isSmallScreen = useMedia({ query: "(max-width: 691px)" });
  const isMediumScreen = useMedia({ query: "(max-width: 991px)" });
  const queryCache = useQueryCache();
  const emptyCache = () => queryCache.invalidateQueries("user");

  useEffect(() => {
    document.title = "My Account";
    // Send to mixpanel
    mixPanelAgencyOnly(site, "Citizen Permit Account Page", 1);
  }, []);

  if (!isLoggedIn) {
    return <LoginRegisterCard />;
  }

  return (
    <PublicWrapper homeUrl={`/${site}/public/account`} contentRef={contentRef} accountPage={true}>
      <Segment className={isSmallScreen ? "small-padding" : isMediumScreen ? "medium-padding" : "large-padding"}>
        <Header as='h1' className='tw-mb-12 tw-mt-4 tw-text-center tw-text-3xl' content='My Account' />
        <Header as='h2' className='my-account-huge-header tw-mb-8'>
          Sign-in and Security
        </Header>
        <AccountInfo label='email' emptyCache={emptyCache} item={user.email} />
        <Divider />
        <AccountInfo label='password' emptyCache={emptyCache} email={user.email} item={"**********"} />
        <Header as='h2' className='my-account-huge-header tw-mb-8 tw-mt-12'>
          Personal Information
        </Header>
        <AccountInfo
          label='name'
          emptyCache={emptyCache}
          defaultValue={{ firstName: user.firstName, lastName: user.lastName }}
          item={`${user.firstName} ${user.lastName}`}
        />
        <Divider />
        <AccountInfo label='phone' emptyCache={emptyCache} item={user.phone} />
        <Divider />
        <AccountInfo
          label='addresses'
          emptyCache={emptyCache}
          item={{
            mailAddress: (
              <FullAddress
                address={address}
                address2={address2}
                city={city}
                state={state}
                zip={zip}
                countryId={showCountryFlag ? countryId : null}
              />
            ),
            physicalAddress: (
              <FullPhysicalAddress
                address={physicalAddress}
                address2={physicalAddress2}
                city={physicalCity}
                state={physicalState}
                zip={physicalZip}
                countryId={showHomeCountryFlag ? homeCountryId : null}
                hasAddress={!!address}
                sameMailAndPhysicalAddress={sameMailAndPhysicalAddress}
              />
            ),
            sameMailAndPhysicalAddress: sameMailAndPhysicalAddress,
          }}
          defaultValue={{
            mailAddress: {
              address: address,
              address2: address2,
              city: city,
              state: state,
              zip: zip,
              countryId: countryId,
            },
            homeAddress: {
              address: physicalAddress,
              address2: physicalAddress2,
              city: physicalCity,
              state: physicalState,
              zip: physicalZip,
              countryId: homeCountryId,
            },
            sameMailAndPhysicalAddress: sameMailAndPhysicalAddress,
          }}
          tooltip={
            <>
              <b>Physical address is used for:</b>
              <ul>
                <li>Service area notifications</li>
                <li>Permits where proof of residency is required</li>
                <li>Service requests</li>
              </ul>
            </>
          }
        />
      </Segment>
    </PublicWrapper>
  );
};

export default CitizenAccount;
